import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Download, Wifi, WifiOff, Play, CheckCircle2, XCircle, 
  ArrowLeft, RefreshCw, Trash2, Clock, Trophy, Home,
  CloudOff, Cloud, Loader2, AlertCircle, BookOpen, 
  Layers, Volume2, RotateCcw, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { offlineStorage } from '@/services/offlineStorage';
import { OposaiLogo } from '@/components/TuPlazaIALogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OfflineTestPage = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState('menu'); // menu, test, flashcards, results
  const [activeTab, setActiveTab] = useState('tests');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Test state
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testStartTime, setTestStartTime] = useState(null);
  const [numQuestions, setNumQuestions] = useState(10);
  
  // Flashcard state
  const [flashcards, setFlashcards] = useState([]);
  const [currentFlashcard, setCurrentFlashcard] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  
  // Results state
  const [results, setResults] = useState(null);

  useEffect(() => {
    loadStats();
    
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadStats = async () => {
    try {
      const offlineStats = await offlineStorage.getStats();
      setStats(offlineStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Download all content for offline use
  const downloadAllContent = async () => {
    if (!isOnline) {
      toast.error('Necesitas conexión a internet para descargar');
      return;
    }

    setDownloading(true);
    try {
      const token = localStorage.getItem('session_token');
      
      // Download ALL questions (limit 10000 for complete offline access)
      const questionsRes = await axios.get(`${API}/offline/package?limit=10000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await offlineStorage.saveQuestions(questionsRes.data.questions);
      const downloadedQuestions = questionsRes.data.questions?.length || 0;
      
      // Download ALL flashcards from user's deck (limit 10000)
      let downloadedFlashcards = 0;
      try {
        const flashcardsRes = await axios.get(`${API}/flashcards/all?limit=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (flashcardsRes.data?.cards) {
          await offlineStorage.saveFlashcards(flashcardsRes.data.cards);
          downloadedFlashcards = flashcardsRes.data.cards.length;
        }
      } catch (e) {
        console.log('No flashcards to download or endpoint not available');
      }
      
      // Also download system flashcards for the user's profession
      try {
        const systemFlashcardsRes = await axios.get(`${API}/flashcards/available?limit=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (systemFlashcardsRes.data?.flashcards) {
          // Merge with user flashcards
          const existingCards = await offlineStorage.getFlashcards();
          const existingIds = new Set(existingCards.map(c => c.flashcard_id || c.card_id));
          const newCards = systemFlashcardsRes.data.flashcards.filter(
            c => !existingIds.has(c.flashcard_id || c.card_id)
          );
          if (newCards.length > 0) {
            await offlineStorage.saveFlashcards([...existingCards, ...newCards]);
            downloadedFlashcards += newCards.length;
          }
        }
      } catch (e) {
        console.log('System flashcards not available');
      }
      
      // Download audio list (metadata only for offline viewing)
      let downloadedAudios = 0;
      try {
        const audiosRes = await axios.get(`${API}/audios?limit=1000`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (audiosRes.data?.audios) {
          await offlineStorage.saveAudios(audiosRes.data.audios);
          downloadedAudios = audiosRes.data.audios.length;
        }
      } catch (e) {
        console.log('No audios to download');
      }
      
      toast.success(`¡Descargado: ${downloadedQuestions} preguntas, ${downloadedFlashcards} flashcards, ${downloadedAudios} audios!`);
      await loadStats();
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar contenido');
    } finally {
      setDownloading(false);
    }
  };

  const syncResults = async () => {
    if (!isOnline) {
      toast.error('Necesitas conexión a internet para sincronizar');
      return;
    }

    setSyncing(true);
    try {
      const pendingResults = await offlineStorage.getPendingResults();
      
      if (pendingResults.length === 0) {
        toast.info('No hay resultados pendientes de sincronizar');
        setSyncing(false);
        return;
      }

      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/offline/sync-results`,
        pendingResults,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const syncedIds = pendingResults.map(r => r.offline_session_id);
      await offlineStorage.clearPendingResults(syncedIds);

      toast.success(`¡${response.data.synced_count} tests sincronizados! +${response.data.xp_earned} XP`);
      await loadStats();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Error al sincronizar resultados');
    } finally {
      setSyncing(false);
    }
  };

  const startOfflineTest = async (count) => {
    try {
      const availableQuestions = await offlineStorage.getQuestions(count);
      
      if (availableQuestions.length === 0) {
        toast.error('No hay preguntas descargadas. Descarga contenido primero.');
        return;
      }

      if (availableQuestions.length < count) {
        toast.warning(`Solo hay ${availableQuestions.length} preguntas disponibles`);
      }

      setQuestions(availableQuestions);
      setCurrentIndex(0);
      setAnswers({});
      setTestStartTime(Date.now());
      setNumQuestions(count);
      setStage('test');
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error('Error al iniciar el test');
    }
  };

  const startFlashcardReview = async () => {
    try {
      const cards = await offlineStorage.getFlashcards();
      
      if (!cards || cards.length === 0) {
        toast.error('No hay flashcards descargadas');
        return;
      }

      // Shuffle cards
      const shuffled = cards.sort(() => Math.random() - 0.5);
      setFlashcards(shuffled);
      setCurrentFlashcard(0);
      setShowAnswer(false);
      setStage('flashcards');
    } catch (error) {
      console.error('Error loading flashcards:', error);
      toast.error('Error al cargar flashcards');
    }
  };

  const handleAnswer = (questionId, optionIndex) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionIndex }));
  };

  const submitOfflineTest = async () => {
    const timeSpent = Math.round((Date.now() - testStartTime) / 1000);
    
    let correctCount = 0;
    const detailedAnswers = questions.map((q) => {
      const selectedOption = answers[q.question_id];
      const isCorrect = selectedOption === q.correct_answer;
      if (isCorrect) correctCount++;
      
      return {
        question_id: q.question_id,
        question_text: q.question,
        selected_option: selectedOption,
        correct_answer: q.correct_answer,
        is_correct: isCorrect,
        explanation: q.explanation || '',
        options: q.options
      };
    });

    const testResult = {
      offline_session_id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      completed_at: new Date().toISOString(),
      questions_count: questions.length,
      correct_count: correctCount,
      score_percentage: Math.round((correctCount / questions.length) * 100),
      time_spent_seconds: timeSpent,
      answers: detailedAnswers
    };

    await offlineStorage.savePendingResult(testResult);
    
    setResults(testResult);
    setStage('results');
    
    toast.success('Test completado. Se sincronizará cuando tengas conexión.');
    await loadStats();
  };

  const clearOfflineData = async () => {
    if (window.confirm('¿Estás seguro de eliminar todo el contenido offline?')) {
      await offlineStorage.clearAll();
      toast.success('Contenido offline eliminado');
      await loadStats();
    }
  };

  // Test stage render
  if (stage === 'test') {
    const currentQuestion = questions[currentIndex];
    const progress = ((currentIndex + 1) / questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
        <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CloudOff className="w-5 h-5 text-orange-500" />
              <span className="font-bold text-slate-700">Test Offline</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                {currentIndex + 1} / {questions.length}
              </span>
              <div className="w-32 bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-orange-600">{currentIndex + 1}</span>
              </div>
              <div className="flex-1">
                {currentQuestion.topic_name && (
                  <p className="text-xs text-orange-600 font-medium mb-2">
                    {currentQuestion.topic_name}
                  </p>
                )}
                <p className="text-lg font-medium text-slate-900">
                  {currentQuestion.question}
                </p>
              </div>
            </div>

            <RadioGroup
              value={answers[currentQuestion.question_id]?.toString() || ''}
              onValueChange={(value) => handleAnswer(currentQuestion.question_id, parseInt(value))}
              className="space-y-3"
            >
              {currentQuestion.options?.map((option, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center space-x-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    answers[currentQuestion.question_id] === idx
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 hover:border-orange-300'
                  }`}
                  onClick={() => handleAnswer(currentQuestion.question_id, idx)}
                >
                  <RadioGroupItem value={idx.toString()} id={`option-${idx}`} />
                  <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                    {typeof option === 'object' ? option.text : option}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex(prev => prev - 1)}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
              </Button>

              {currentIndex < questions.length - 1 ? (
                <Button
                  onClick={() => setCurrentIndex(prev => prev + 1)}
                  className="rounded-full bg-orange-500 hover:bg-orange-600"
                >
                  Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={submitOfflineTest}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Finalizar Test
                </Button>
              )}
            </div>
          </div>

          {/* Question navigator */}
          <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-600 mb-3">Navegación rápida:</p>
            <div className="flex flex-wrap gap-2">
              {questions.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                    idx === currentIndex
                      ? 'bg-orange-500 text-white'
                      : answers[questions[idx]?.question_id] !== undefined
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Flashcards stage render
  if (stage === 'flashcards') {
    const card = flashcards[currentFlashcard];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStage('menu')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </Button>
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-500" />
              <span className="font-bold text-slate-700">Flashcards Offline</span>
            </div>
            <span className="text-sm text-slate-600">
              {currentFlashcard + 1} / {flashcards.length}
            </span>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-4 py-8">
          <div 
            className={`bg-white rounded-3xl border-2 shadow-lg p-8 min-h-[300px] flex flex-col items-center justify-center cursor-pointer transition-all ${
              showAnswer ? 'border-purple-300' : 'border-slate-200'
            }`}
            onClick={() => setShowAnswer(!showAnswer)}
          >
            {!showAnswer ? (
              <>
                <p className="text-xs text-purple-600 font-medium mb-4">PREGUNTA</p>
                <p className="text-xl font-medium text-slate-900 text-center">
                  {card?.front || card?.question || 'Sin pregunta'}
                </p>
                <p className="text-sm text-slate-400 mt-6">Toca para ver la respuesta</p>
              </>
            ) : (
              <>
                <p className="text-xs text-emerald-600 font-medium mb-4">RESPUESTA</p>
                <p className="text-xl font-medium text-slate-900 text-center">
                  {card?.back || card?.answer || 'Sin respuesta'}
                </p>
              </>
            )}
          </div>

          <div className="flex justify-between mt-8 gap-4">
            <Button
              variant="outline"
              disabled={currentFlashcard === 0}
              onClick={() => { setCurrentFlashcard(prev => prev - 1); setShowAnswer(false); }}
              className="flex-1 rounded-full"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
            </Button>
            
            <Button
              variant="outline"
              onClick={() => { 
                setFlashcards(flashcards.sort(() => Math.random() - 0.5)); 
                setCurrentFlashcard(0); 
                setShowAnswer(false);
                toast.success('Flashcards mezcladas');
              }}
              className="rounded-full"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>

            <Button
              disabled={currentFlashcard === flashcards.length - 1}
              onClick={() => { setCurrentFlashcard(prev => prev + 1); setShowAnswer(false); }}
              className="flex-1 rounded-full bg-purple-500 hover:bg-purple-600"
            >
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Difficulty buttons - only show when answer is visible */}
          {showAnswer && (
            <div className="mt-6">
              <p className="text-center text-sm text-slate-500 mb-3">¿Qué tal te fue con esta tarjeta?</p>
              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => { 
                    // Mark as difficult - will show again soon
                    const updatedCards = [...flashcards];
                    const card = updatedCards.splice(currentFlashcard, 1)[0];
                    // Insert it 3 cards ahead
                    const insertPos = Math.min(currentFlashcard + 3, updatedCards.length);
                    updatedCards.splice(insertPos, 0, card);
                    setFlashcards(updatedCards);
                    setShowAnswer(false);
                    toast('🔴 Marcada para repasar pronto', { duration: 1500 });
                  }}
                  className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                >
                  😓 Difícil
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { 
                    // Mark as medium - show later
                    if (currentFlashcard < flashcards.length - 1) {
                      setCurrentFlashcard(prev => prev + 1);
                    }
                    setShowAnswer(false);
                    toast('🟡 Normal', { duration: 1500 });
                  }}
                  className="rounded-full border-amber-200 text-amber-600 hover:bg-amber-50"
                >
                  🤔 Normal
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { 
                    // Mark as easy - remove from review
                    const updatedCards = flashcards.filter((_, i) => i !== currentFlashcard);
                    if (updatedCards.length === 0) {
                      toast.success('¡Has completado todas las flashcards!');
                      setStage('menu');
                      return;
                    }
                    setFlashcards(updatedCards);
                    // Stay on same index (which now shows next card)
                    if (currentFlashcard >= updatedCards.length) {
                      setCurrentFlashcard(updatedCards.length - 1);
                    }
                    setShowAnswer(false);
                    toast('🟢 ¡Dominada!', { duration: 1500 });
                  }}
                  className="rounded-full border-green-200 text-green-600 hover:bg-green-50"
                >
                  😊 Fácil
                </Button>
              </div>
              <p className="text-center text-xs text-slate-400 mt-2">
                Quedan {flashcards.length - 1} tarjetas en este repaso
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Results stage render
  if (stage === 'results') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
        <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStage('menu')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </Button>
            <OposaiLogo size={36} showText={false} />
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-lg mb-8 text-center">
            <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center ${
              results.score_percentage >= 70 ? 'bg-emerald-100' : 
              results.score_percentage >= 50 ? 'bg-amber-100' : 'bg-red-100'
            }`}>
              <span className={`text-4xl font-bold ${
                results.score_percentage >= 70 ? 'text-emerald-600' : 
                results.score_percentage >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {results.score_percentage}%
              </span>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {results.score_percentage >= 70 ? '¡Excelente trabajo!' : 
               results.score_percentage >= 50 ? '¡Buen intento!' : '¡Sigue practicando!'}
            </h2>
            
            <p className="text-slate-600 mb-6">
              Has acertado {results.correct_count} de {results.questions_count} preguntas
            </p>

            <div className="flex justify-center gap-6 text-sm">
              <div className="text-center">
                <Clock className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                <span className="text-slate-600">
                  {Math.floor(results.time_spent_seconds / 60)}:{(results.time_spent_seconds % 60).toString().padStart(2, '0')}
                </span>
              </div>
              <div className="text-center">
                <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
                <span className="text-emerald-600">{results.correct_count} correctas</span>
              </div>
              <div className="text-center">
                <XCircle className="w-5 h-5 mx-auto mb-1 text-red-500" />
                <span className="text-red-600">{results.questions_count - results.correct_count} incorrectas</span>
              </div>
            </div>

            {!isOnline && (
              <div className="mt-6 p-3 bg-amber-50 rounded-xl flex items-center justify-center gap-2 text-amber-700">
                <WifiOff className="w-4 h-4" />
                <span className="text-sm">Resultados guardados. Se sincronizarán al conectar.</span>
              </div>
            )}
          </div>

          <h3 className="text-lg font-bold text-slate-900 mb-4">Revisión de respuestas</h3>
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {results.answers.map((answer, idx) => (
              <div 
                key={idx} 
                className={`bg-white rounded-xl border p-4 ${
                  answer.is_correct ? 'border-emerald-200' : 'border-red-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    answer.is_correct ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {answer.is_correct ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 mb-2">{answer.question_text}</p>
                    
                    {!answer.is_correct && answer.selected_option !== undefined && (
                      <p className="text-sm text-red-600 mb-1">
                        Tu respuesta: {typeof answer.options[answer.selected_option] === 'object' 
                          ? answer.options[answer.selected_option].text 
                          : answer.options[answer.selected_option]}
                      </p>
                    )}
                    
                    <p className="text-sm text-emerald-600">
                      Respuesta correcta: {typeof answer.options[answer.correct_answer] === 'object'
                        ? answer.options[answer.correct_answer].text
                        : answer.options[answer.correct_answer]}
                    </p>
                    
                    {answer.explanation && (
                      <p className="text-sm text-slate-500 mt-2 italic bg-slate-50 p-2 rounded">
                        {answer.explanation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-4 mt-8">
            <Button
              onClick={() => setStage('menu')}
              variant="outline"
              className="flex-1 rounded-full py-6"
            >
              <Home className="w-4 h-4 mr-2" /> Menú Offline
            </Button>
            <Button
              onClick={() => startOfflineTest(numQuestions)}
              className="flex-1 rounded-full py-6 bg-orange-500 hover:bg-orange-600"
            >
              <Play className="w-4 h-4 mr-2" /> Nuevo Test
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Menu stage render
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </Button>
            <div className="flex items-center gap-2">
              <OposaiLogo size={36} showText={false} />
              <h1 className="text-xl font-bold">Modo Offline</h1>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
            }`}>
              {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span className="text-sm font-medium">{isOnline ? 'Online' : 'Offline'}</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Stats Card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Contenido Descargado</h2>
            {stats?.pendingResultsCount > 0 && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                {stats.pendingResultsCount} pendiente{stats.pendingResultsCount > 1 ? 's' : ''}
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <BookOpen className="w-6 h-6 mx-auto mb-2 text-orange-500" />
              <p className="text-xl font-bold text-slate-900">{stats?.questionCount || 0}</p>
              <p className="text-xs text-slate-600">Preguntas</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <Layers className="w-6 h-6 mx-auto mb-2 text-purple-500" />
              <p className="text-xl font-bold text-slate-900">{stats?.flashcardCount || 0}</p>
              <p className="text-xs text-slate-600">Flashcards</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <Volume2 className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-xl font-bold text-slate-900">{stats?.audioCount || 0}</p>
              <p className="text-xs text-slate-600">Audios</p>
            </div>
          </div>

          {stats?.lastDownload && (
            <p className="text-xs text-slate-500 mt-4 text-center">
              Última descarga: {new Date(stats.lastDownload).toLocaleDateString('es-ES')}
            </p>
          )}
        </div>

        {/* Download Section */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-orange-500" />
            Descargar Contenido
          </h3>
          <p className="text-slate-600 mb-4 text-sm">
            Descarga preguntas, flashcards y audios para estudiar sin conexión.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={downloadAllContent}
              disabled={downloading || !isOnline}
              className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Descargando...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Descargar Todo</>
              )}
            </Button>
            {(stats?.questionCount > 0 || stats?.flashcardCount > 0) && (
              <Button
                onClick={clearOfflineData}
                variant="outline"
                className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content Tabs */}
        {stats?.questionCount > 0 && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="tests" className="rounded-full">
                <BookOpen className="w-4 h-4 mr-2" /> Tests
              </TabsTrigger>
              <TabsTrigger value="flashcards" className="rounded-full">
                <Layers className="w-4 h-4 mr-2" /> Flashcards
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tests">
              <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-6 shadow-lg text-white">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Play className="w-5 h-5" /> Iniciar Test Offline
                </h3>
                <p className="text-orange-100 mb-4 text-sm">
                  Los resultados se guardan y sincronizan automáticamente.
                </p>
                
                <p className="text-sm text-orange-100 mb-2">Número de preguntas:</p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[10, 20, 30, 50, 72, 100].map((num) => (
                    <button
                      key={num}
                      onClick={() => setNumQuestions(num)}
                      disabled={stats?.questionCount < num}
                      className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                        numQuestions === num
                          ? 'bg-white text-orange-600'
                          : stats?.questionCount >= num
                          ? 'bg-white/20 text-white hover:bg-white/30'
                          : 'bg-white/10 text-white/50 cursor-not-allowed'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>

                <Button
                  onClick={() => startOfflineTest(numQuestions)}
                  className="w-full rounded-full py-5 bg-white text-orange-600 hover:bg-orange-50 font-bold"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Comenzar Test de {numQuestions} preguntas
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="flashcards">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl p-6 shadow-lg text-white">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <Layers className="w-5 h-5" /> Repasar Flashcards
                </h3>
                <p className="text-purple-100 mb-4 text-sm">
                  Repasa tus flashcards descargadas sin conexión.
                </p>
                
                <div className="bg-white/20 rounded-xl p-4 mb-4">
                  <p className="text-2xl font-bold">{stats?.flashcardCount || 0}</p>
                  <p className="text-purple-100 text-sm">flashcards disponibles</p>
                </div>

                <Button
                  onClick={startFlashcardReview}
                  disabled={!stats?.flashcardCount}
                  className="w-full rounded-full py-5 bg-white text-purple-600 hover:bg-purple-50 font-bold"
                >
                  <Layers className="w-5 h-5 mr-2" />
                  {stats?.flashcardCount ? 'Empezar Repaso' : 'No hay flashcards'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Sync Section */}
        {stats?.pendingResultsCount > 0 && (
          <div className="bg-white rounded-2xl border border-amber-200 p-6 shadow-sm mt-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Cloud className="w-5 h-5 text-amber-500" />
              Sincronizar Resultados
            </h3>
            <p className="text-slate-600 mb-4 text-sm">
              Tienes {stats.pendingResultsCount} test{stats.pendingResultsCount > 1 ? 's' : ''} pendiente{stats.pendingResultsCount > 1 ? 's' : ''}.
            </p>
            <Button
              onClick={syncResults}
              disabled={syncing || !isOnline}
              className="w-full rounded-full bg-amber-500 hover:bg-amber-600"
            >
              {syncing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sincronizando...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Sincronizar ahora</>
              )}
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!stats?.questionCount && (
          <div className="text-center py-12">
            <CloudOff className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-bold text-slate-700 mb-2">
              No hay contenido descargado
            </h3>
            <p className="text-slate-500 mb-4">
              Descarga contenido mientras tengas conexión
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineTestPage;
